import * as THREE from 'three';
import { Entity } from './Entity.js';
import { CONFIG, ASCII_CHARS, WEAPON_TYPES } from '../core/config.js';
import { Bullet } from './Bullet.js';

export class Player extends Entity {
    constructor(x, y, scene, characterData = null) {
        // Use character data for visual appearance, fallback to defaults
        const char = characterData?.char || ASCII_CHARS.PLAYER;
        const color = characterData?.color || CONFIG.COLORS.PLAYER;
        
        super(x, y, char, color, scene, CONFIG.PLAYER_SIZE);
        
        this.isPlayer = true; // Mark this as player for special effects
        
        // Recreate mesh with player glow effect since isPlayer was set after super()
        if (this.mesh) {
            this.scene.remove(this.mesh);
            if (this.mesh.material) this.mesh.material.dispose();
            if (this.mesh.geometry) this.mesh.geometry.dispose();
        }
        this.createMesh();
        this.characterData = characterData;
        
        // Apply character-specific stats
        if (characterData) {
            this.speed = CONFIG.PLAYER_SPEED * characterData.stats.speedMod;
            this.expMultiplier = characterData.stats.expMod;
            
            // Apply character abilities
            this.applyCharacterAbilities(characterData);
        } else {
            this.speed = CONFIG.PLAYER_SPEED;
        }
        
        this.regen = 0;
        this.expMultiplier = 1;
        this.critChance = 0.05; // 5% crit chance
        this.critMultiplier = 2.0;
        this.damageReduction = 0;
        this.magnetRange = 1;
        
        // New weapon system - multiple active weapons
        this.activeWeapons = new Map(); // weaponType -> weaponData
        this.weaponSlots = 6; // Maximum weapon slots like Vampire Survivors
        this.weaponCooldowns = new Map(); // weaponType -> cooldown
        
        // Start with character's base weapon
        const startingWeapon = characterData?.startingWeapon || 'ASCII_BEAM';
        this.addWeapon(startingWeapon);
        
        this.keys = {};
        this.setupInputHandlers();
    }
    
    setupInputHandlers() {
        window.addEventListener('keydown', (event) => {
            this.keys[event.key] = true;
            event.preventDefault();
        });
        
        window.addEventListener('keyup', (event) => {
            this.keys[event.key] = false;
            event.preventDefault();
        });
    }
    
    update(deltaTime, camera, gameState) {
        super.update(deltaTime, camera);
        
        this.handleMovement(deltaTime);
        this.handleWeapons(deltaTime, gameState);
        this.handleRegeneration(deltaTime, gameState);
    }
    
    handleMovement(deltaTime) {
        const moveVector = new THREE.Vector3();
        
        if (this.keys['w'] || this.keys['W'] || this.keys['ArrowUp']) moveVector.z -= 1;
        if (this.keys['s'] || this.keys['S'] || this.keys['ArrowDown']) moveVector.z += 1;
        if (this.keys['a'] || this.keys['A'] || this.keys['ArrowLeft']) moveVector.x -= 1;
        if (this.keys['d'] || this.keys['D'] || this.keys['ArrowRight']) moveVector.x += 1;
        
        if (moveVector.length() > 0) {
            moveVector.normalize();
            this.position.add(moveVector.multiplyScalar(this.speed * deltaTime));
            
            // No world boundaries for infinite map
            // The camera will follow the player, creating an infinite feel
        }
    }
    
    handleWeapons(deltaTime, gameState) {
        // Update all weapon cooldowns
        for (const [weaponType, cooldown] of this.weaponCooldowns) {
            this.weaponCooldowns.set(weaponType, Math.max(0, cooldown - deltaTime));
        }
        
        // Fire each weapon independently
        for (const [weaponType, weaponData] of this.activeWeapons) {
            const cooldown = this.weaponCooldowns.get(weaponType) || 0;
            if (cooldown <= 0) {
                const target = this.findNearestEnemy(gameState.enemies, weaponType);
                if (target) {
                    this.fireWeapon(weaponType, weaponData, target, gameState);
                    const baseWeapon = WEAPON_TYPES[weaponType];
                    const fireRate = baseWeapon.fireRate * (1 + weaponData.level * 0.1); // Increase fire rate with level
                    this.weaponCooldowns.set(weaponType, 1 / fireRate);
                }
            }
        }
    }
    
    addWeapon(weaponType) {
        if (this.activeWeapons.has(weaponType)) {
            // Upgrade existing weapon
            const weaponData = this.activeWeapons.get(weaponType);
            weaponData.level = Math.min(weaponData.level + 1, 5); // Max level 5
            return true;
        } else if (this.activeWeapons.size < this.weaponSlots) {
            // Add new weapon
            this.activeWeapons.set(weaponType, { level: 1 });
            this.weaponCooldowns.set(weaponType, 0);
            return true;
        }
        return false; // No space for new weapon
    }
    
    getWeaponLevel(weaponType) {
        return this.activeWeapons.get(weaponType)?.level || 0;
    }
    
    hasWeapon(weaponType) {
        return this.activeWeapons.has(weaponType);
    }
    
    applyCharacterAbilities(characterData) {
        switch(characterData.id) {
            case 'HACKER':
                // Deep Strike: All weapons gain +1 piercing
                this.bonusPiercing = 1;
                break;
            case 'GUARDIAN':
                // Fortified: 25% damage reduction
                this.damageReduction = 0.25;
                break;
            case 'ARCHITECT':
                // Cascade: +50% explosion radius
                this.explosionRadiusMultiplier = 1.5;
                break;
            case 'SPEEDRUNNER':
                // Overclock: No movement penalty while firing
                this.noMovementPenalty = true;
                break;
            case 'SYNTHESIZER':
                // Resonance: Chain effects jump to more targets
                this.chainBonusTargets = 2;
                break;
        }
    }
    
    handleRegeneration(deltaTime, gameState) {
        if (this.regen > 0) {
            gameState.stats.health = Math.min(
                gameState.stats.maxHealth, 
                gameState.stats.health + this.regen * deltaTime
            );
        }
    }
    
    findNearestEnemy(enemies, weaponType) {
        let nearest = null;
        const weapon = WEAPON_TYPES[weaponType];
        let nearestDist = weapon.range;
        
        enemies.forEach(enemy => {
            const dist = this.getDistanceTo(enemy);
            if (dist < nearestDist) {
                nearest = enemy;
                nearestDist = dist;
            }
        });
        
        return nearest;
    }
    
    fireWeapon(weaponType, weaponData, target, gameState) {
        const weapon = WEAPON_TYPES[weaponType];
        const isCrit = Math.random() < this.critChance;
        
        // Calculate weapon damage with level scaling
        const baseDamage = weapon.damage;
        const levelMultiplier = 1 + (weaponData.level - 1) * 0.25; // +25% damage per level
        const scaledWeapon = { 
            ...weapon, 
            damage: baseDamage * levelMultiplier 
        };
        
        // Play shooting sound
        if (window.gameAudio) {
            window.gameAudio.playShoot();
        }
        
        if (scaledWeapon.spreadCount) {
            // Spread shot weapon
            const spreadCount = scaledWeapon.spreadCount + Math.floor((weaponData.level - 1) / 2); // More projectiles with level
            const angleStep = Math.PI / 6; // 30 degrees
            const startAngle = -angleStep * (spreadCount - 1) / 2;
            
            for (let i = 0; i < spreadCount; i++) {
                const angle = startAngle + angleStep * i;
                const direction = this.getDirectionTo(target);
                const rotatedDirection = new THREE.Vector3(
                    direction.x * Math.cos(angle) - direction.z * Math.sin(angle),
                    0,
                    direction.x * Math.sin(angle) + direction.z * Math.cos(angle)
                );
                
                const targetX = this.position.x + rotatedDirection.x * scaledWeapon.range;
                const targetY = this.position.z + rotatedDirection.z * scaledWeapon.range;
                
                const bullet = new Bullet(
                    this.position.x, 
                    this.position.z, 
                    targetX,
                    targetY,
                    scaledWeapon,
                    isCrit,
                    this.scene
                );
                
                gameState.bullets.push(bullet);
            }
        } else {
            // Single shot weapon
            const bullet = new Bullet(
                this.position.x, 
                this.position.z, 
                target.position.x, 
                target.position.z,
                scaledWeapon,
                isCrit,
                this.scene
            );
            
            gameState.bullets.push(bullet);
        }
    }
    
    takeDamage(amount, isCritical = false) {
        const wasDead = super.takeDamage(amount, isCritical);
        
        if (this === window.gamePlayer) { // Global player reference
            document.getElementById('gameContainer').classList.add('screen-shake');
            setTimeout(() => {
                document.getElementById('gameContainer').classList.remove('screen-shake');
            }, 200);
        }
        
        return wasDead;
    }
} 